import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, writeBatch, setDoc, query, where } from 'firebase/firestore';
import { cancelSubscription as cancelStripeSubscription } from './stripeService';

// Import AuthContext for referral system updates
// import { useContext } from 'react';
// import { AuthContext } from '../contexts/AuthContext';

// Subscription plan types
export const SUBSCRIPTION_TYPES = {
  PLAN_1: 'plan_1',
  PLAN_2: 'plan_2',
  PLAN_3: 'plan_3',
  PLAN_4: 'plan_4',
  PLAN_5: 'plan_5',
};

// Flex credit package types
export const FLEX_CREDIT_TYPES = {
  FLEX_1: 'flex_1',
  FLEX_2: 'flex_2',
  FLEX_3: 'flex_3',
  FLEX_4: 'flex_4',
  FLEX_5: 'flex_5',
};

// Default subscription plans
const DEFAULT_SUBSCRIPTION_PLANS = {
  [SUBSCRIPTION_TYPES.PLAN_1]: {
    name: 'Basic',
    monthlyCredits: 50,
    price: 9.99,
    features: ['Basic features'],
    sortOrder: 1,
    enabled: true,
    stripeProductId: '',
    stripePriceId: '',
    maxProductSelection: 5
  },
  [SUBSCRIPTION_TYPES.PLAN_2]: {
    name: 'Standard',
    monthlyCredits: 150,
    price: 19.99,
    features: ['Standard features', 'Priority support'],
    sortOrder: 2,
    enabled: true,
    stripeProductId: '',
    stripePriceId: '',
    maxProductSelection: 15
  },
  [SUBSCRIPTION_TYPES.PLAN_3]: {
    name: 'Premium',
    monthlyCredits: 500,
    price: 49.99,
    features: ['Premium features', 'Priority support', 'Advanced analytics'],
    sortOrder: 3,
    enabled: true,
    stripeProductId: '',
    stripePriceId: '',
    maxProductSelection: 50
  },
  [SUBSCRIPTION_TYPES.PLAN_4]: {
    name: 'Enterprise',
    monthlyCredits: 1000,
    price: 99.99,
    features: ['Enterprise features', 'Priority support', 'Advanced analytics', 'Dedicated account manager'],
    sortOrder: 4,
    enabled: true,
    stripeProductId: '',
    stripePriceId: '',
    maxProductSelection: 100
  },
  [SUBSCRIPTION_TYPES.PLAN_5]: {
    name: 'Ultimate',
    monthlyCredits: 2500,
    price: 199.99,
    features: ['Ultimate features', 'Priority support', 'Advanced analytics', 'Dedicated account manager', 'Custom integrations'],
    sortOrder: 5,
    enabled: true,
    stripeProductId: '',
    stripePriceId: '',
    maxProductSelection: 250
  }
};

// Default flex credit packages
const DEFAULT_FLEX_CREDIT_PACKAGES = {
  [FLEX_CREDIT_TYPES.FLEX_1]: {
    name: 'Small Pack',
    credits: 50,
    price: 4.99,
    sortOrder: 1,
    enabled: true,
    stripeProductId: '',
    stripePriceId: ''
  },
  [FLEX_CREDIT_TYPES.FLEX_2]: {
    name: 'Medium Pack',
    credits: 150,
    price: 12.99,
    sortOrder: 2,
    enabled: true,
    stripeProductId: '',
    stripePriceId: ''
  },
  [FLEX_CREDIT_TYPES.FLEX_3]: {
    name: 'Large Pack',
    credits: 500,
    price: 39.99,
    sortOrder: 3,
    enabled: true,
    stripeProductId: '',
    stripePriceId: ''
  },
  [FLEX_CREDIT_TYPES.FLEX_4]: {
    name: 'XL Pack',
    credits: 1000,
    price: 69.99,
    sortOrder: 4,
    enabled: true,
    stripeProductId: '',
    stripePriceId: ''
  },
  [FLEX_CREDIT_TYPES.FLEX_5]: {
    name: 'XXL Pack',
    credits: 2500,
    price: 149.99,
    sortOrder: 5,
    enabled: true,
    stripeProductId: '',
    stripePriceId: ''
  }
};

// Initialize subscription plans in Firestore if they don't exist
export const initializeSubscriptionPlans = async () => {
  try {
    const plansRef = collection(db, 'subscriptionPlans');
    const plansSnapshot = await getDocs(plansRef);
    
    if (plansSnapshot.empty) {
      console.log('No subscription plans found, initializing default plans');
      const batch = writeBatch(db);
      
      // Plan 1: Basic
      batch.set(doc(plansRef, SUBSCRIPTION_TYPES.PLAN_1), {
        name: 'Basic',
        description: 'Essential features for small stores',
        price: 9.99,
        compareAtPrice: null, // Regular price when on sale
        salePercentage: 0, // No sale by default
        monthlyCredits: 100,
        features: [
          'Edit up to 5 products at once',
          '100 monthly credits',
          'Price Tools',
          'Inventory Tools',
          'Product Tools',
        ],
        enabled: true,
        sortOrder: 1,
        maxProductSelection: 5
      });
      
      // Plan 2: Standard
      batch.set(doc(plansRef, SUBSCRIPTION_TYPES.PLAN_2), {
        name: 'Standard',
        description: 'Advanced features for growing stores',
        price: 19.99,
        compareAtPrice: null,
        salePercentage: 0,
        monthlyCredits: 300,
        features: [
          'Edit up to 15 products at once',
          '300 monthly credits',
          'Price Tools',
          'Inventory Tools',
          'Product Tools',
          'Priority Support',
        ],
        enabled: true,
        sortOrder: 2,
        maxProductSelection: 15
      });
      
      // Plan 3: Premium
      batch.set(doc(plansRef, SUBSCRIPTION_TYPES.PLAN_3), {
        name: 'Premium',
        description: 'Premium features for established stores',
        price: 39.99,
        compareAtPrice: null,
        salePercentage: 0,
        monthlyCredits: 1000,
        features: [
          'Edit up to 50 products at once',
          '1000 monthly credits',
          'Price Tools',
          'Inventory Tools',
          'Product Tools',
          'Priority Support',
          'Advanced Analytics',
        ],
        enabled: true,
        sortOrder: 3,
        maxProductSelection: 50
      });
      
      // Plan 4: Enterprise
      batch.set(doc(plansRef, SUBSCRIPTION_TYPES.PLAN_4), {
        name: 'Enterprise',
        description: 'Enterprise-grade features for large stores',
        price: 79.99,
        compareAtPrice: null,
        salePercentage: 0,
        monthlyCredits: 2500,
        features: [
          'Edit up to 100 products at once',
          '2500 monthly credits',
          'Price Tools',
          'Inventory Tools',
          'Product Tools',
          'Priority Support',
          'Advanced Analytics',
          'Custom Integrations',
        ],
        enabled: true,
        sortOrder: 4,
        maxProductSelection: 100
      });
      
      // Plan 5: Ultimate
      batch.set(doc(plansRef, SUBSCRIPTION_TYPES.PLAN_5), {
        name: 'Ultimate',
        description: 'Ultimate features for enterprise stores',
        price: 149.99,
        compareAtPrice: null,
        salePercentage: 0,
        monthlyCredits: 5000,
        features: [
          'Edit up to 250 products at once',
          '5000 monthly credits',
          'Price Tools',
          'Inventory Tools',
          'Product Tools',
          'Priority Support',
          'Advanced Analytics',
          'Custom Integrations',
          'Dedicated Account Manager',
        ],
        enabled: true,
        sortOrder: 5,
        maxProductSelection: 250
      });
      
      await batch.commit();
      console.log('Default subscription plans initialized');
    } else {
      console.log('Subscription plans already exist');
    }
  } catch (error) {
    console.error('Error initializing subscription plans:', error);
  }
};

// Get all subscription plans
export const getSubscriptionPlans = async () => {
  try {
    const plansRef = collection(db, 'subscriptionPlans');
    const snapshot = await getDocs(plansRef);
    
    if (snapshot.empty) {
      await initializeSubscriptionPlans();
      return getSubscriptionPlans();
    }
    
    const plans = {};
    snapshot.forEach(doc => {
      plans[doc.id] = doc.data();
    });
    
    return plans;
  } catch (error) {
    console.error('Error getting subscription plans:', error);
    return DEFAULT_SUBSCRIPTION_PLANS;
  }
};

// Update a subscription plan
export const updateSubscriptionPlan = async (planType, updates) => {
  try {
    // Validate plan type
    if (!Object.values(SUBSCRIPTION_TYPES).includes(planType)) {
      throw new Error(`Invalid plan type: ${planType}`);
    }
    
    const planRef = doc(db, 'subscriptionPlans', planType);
    const planDoc = await getDoc(planRef);
    
    if (!planDoc.exists()) {
      throw new Error(`Plan ${planType} not found`);
    }
    
    const currentPlan = planDoc.data();
    
    // Calculate compareAtPrice based on salePercentage if provided
    let compareAtPrice = updates.compareAtPrice;
    let price = parseFloat(updates.price) || currentPlan.price;
    let salePercentage = updates.salePercentage !== undefined ? parseFloat(updates.salePercentage) : currentPlan.salePercentage;
    
    // If salePercentage is provided but compareAtPrice is not, calculate it
    if (salePercentage > 0 && !compareAtPrice) {
      // Ensure the price ends with the same cents as the original price
      const priceCents = Math.round((price % 1) * 100);
      const rawComparePrice = price / (1 - salePercentage / 100);
      // Format to 2 decimal places and then ensure it ends with the same cents
      const formattedPrice = Math.floor(rawComparePrice) + (priceCents / 100);
      compareAtPrice = parseFloat(formattedPrice.toFixed(2));
    } 
    // If compareAtPrice is provided but salePercentage is not, calculate it
    else if (compareAtPrice && salePercentage === 0) {
      compareAtPrice = parseFloat(compareAtPrice);
      if (compareAtPrice > price) {
        salePercentage = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
      }
    }
    // If both are 0 or null, reset both
    else if (salePercentage === 0) {
      compareAtPrice = null;
    }
    
    // Prepare update object
    const planUpdates = {
      ...updates,
      price: price,
      compareAtPrice: compareAtPrice,
      salePercentage: salePercentage,
      updatedAt: new Date().toISOString()
    };
    
    // Remove undefined values
    Object.keys(planUpdates).forEach(key => {
      if (planUpdates[key] === undefined) {
        delete planUpdates[key];
      }
    });
    
    await updateDoc(planRef, planUpdates);
    
    return {
      ...currentPlan,
      ...planUpdates
    };
  } catch (error) {
    console.error('Error updating subscription plan:', error);
    throw error;
  }
};

// Get user's subscription
export const getUserSubscription = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    // Check for regular subscription
    const subscription = userData?.subscription || null;
    
    // Check for VIP status
    const isVIP = userData?.isVIP || false;
    const vipSubscription = userData?.vipSubscription || null;
    
    // If user has a subscription, ensure it has a proper nextBillingDate for monthly credits
    if (subscription && subscription.status === 'active') {
      // For yearly subscriptions, we need to calculate the next monthly credit reset date
      if (subscription.billingPeriod === 'yearly' && !subscription.nextBillingDate) {
        const startDate = subscription.currentPeriodStart || new Date(subscription.periodStartTimestamp * 1000);
        const nextReset = calculateNextMonthlyResetDate(startDate);
        
        // Update the subscription with the next monthly reset date
        await updateDoc(userRef, {
          'subscription.nextBillingDate': nextReset.toISOString()
        });
        
        // Update the local subscription object
        subscription.nextBillingDate = nextReset.toISOString();
      }
    }
    
    // If user has a VIP subscription, ensure it has a proper nextBillingDate
    if (isVIP && vipSubscription && !vipSubscription.nextBillingDate) {
      const startDate = vipSubscription.startDate;
      const nextReset = calculateNextMonthlyResetDate(startDate);
      
      // Update the VIP subscription with the next monthly reset date
      await updateDoc(userRef, {
        'vipSubscription.nextBillingDate': nextReset.toISOString()
      });
      
      // Update the local VIP subscription object
      vipSubscription.nextBillingDate = nextReset.toISOString();
    }
    
    return {
      subscription,
      isVIP,
      vipSubscription
    };
  } catch (error) {
    console.error('Error getting user subscription:', error);
    throw error;
  }
};

// Calculate the next monthly credit reset date
export const calculateNextMonthlyResetDate = (startDate) => {
  // If no start date provided, use current date
  const baseDate = startDate ? new Date(startDate) : new Date();
  
  // Get the current date
  const now = new Date();
  
  // Create a new date for the next monthly reset
  const nextReset = new Date(baseDate);
  
  // Set the day to the same day of the month as the start date
  // Then advance months until we find a date in the future
  while (nextReset <= now) {
    nextReset.setMonth(nextReset.getMonth() + 1);
  }
  
  return nextReset;
};

// Set a subscription for a user
export const setUserSubscription = async (userId, subscriptionType, adminOverride = false) => {
  try {
    // Get the subscription plan details
    const plans = await getSubscriptionPlans();
    const plan = plans[subscriptionType];
    
    if (!plan) {
      throw new Error(`Invalid subscription type: ${subscriptionType}`);
    }
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    // Calculate next billing date (1 month from now or from previous billing date)
    const now = new Date();
    let nextBillingDate;
    
    if (userData?.subscription?.nextBillingDate) {
      // If user already has a subscription, set next billing date to 1 month from previous billing date
      const prevDate = new Date(userData.subscription.nextBillingDate);
      nextBillingDate = new Date(prevDate.setMonth(prevDate.getMonth() + 1));
    } else {
      // New subscription, set next billing date to 1 month from now
      nextBillingDate = new Date(now.setMonth(now.getMonth() + 1));
    }
    
    // Create subscription object
    const subscription = {
      type: subscriptionType,
      planName: plan.name,
      monthlyCredits: plan.monthlyCredits,
      price: plan.price,
      startDate: new Date().toISOString(),
      nextBillingDate: nextBillingDate.toISOString(),
      status: 'active',
      adminOverride: adminOverride
    };
    
    // Reset monthly credits and update subscription
    await updateDoc(userRef, {
      subscription,
      'credits.monthly': plan.monthlyCredits,
      'credits.lastRefillDate': new Date().toISOString()
    });
    
    // Update referral system with subscription data
    await updateReferralSystemWithSubscription(userId, subscription);
    
    return subscription;
  } catch (error) {
    console.error('Error setting user subscription:', error);
    throw error;
  }
};

// Cancel a user's subscription
export const cancelUserSubscription = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData?.subscription) {
      return false; // No subscription to cancel
    }
    
    // Update subscription status to canceled
    await updateDoc(userRef, {
      'subscription.status': 'canceled',
      'subscription.cancelDate': new Date().toISOString()
    });
    
    // Update referral system with canceled subscription
    const updatedSubscription = {
      ...userData.subscription,
      status: 'canceled',
      cancelDate: new Date().toISOString()
    };
    await updateReferralSystemWithSubscription(userId, updatedSubscription);
    
    return true;
  } catch (error) {
    console.error('Error canceling user subscription:', error);
    return false;
  }
};

// Add flex credits to a user
export const addFlexCredits = async (userId, amount) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    const currentFlexCredits = userData?.credits?.flex || 0;
    
    await updateDoc(userRef, {
      'credits.flex': currentFlexCredits + parseInt(amount)
    });
    
    // Record the flex credits purchase
    await setDoc(doc(collection(db, 'users', userId, 'flexCreditPurchases')), {
      amount: parseInt(amount),
      timestamp: new Date().toISOString(),
      adminAdded: true // Flag to indicate this was added by admin
    });
    
    return true;
  } catch (error) {
    console.error('Error adding flex credits:', error);
    return false;
  }
};

// Purchase flex credits (for user self-service)
export const purchaseFlexCredits = async (userId, amount) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    const currentFlexCredits = userData?.credits?.flex || 0;
    
    await updateDoc(userRef, {
      'credits.flex': currentFlexCredits + parseInt(amount)
    });
    
    // Record the flex credits purchase
    await setDoc(doc(collection(db, 'users', userId, 'flexCreditPurchases')), {
      amount: parseInt(amount),
      timestamp: new Date().toISOString(),
      adminAdded: false, // User purchased these credits
      price: calculateFlexCreditPrice(amount)
    });
    
    return true;
  } catch (error) {
    console.error('Error purchasing flex credits:', error);
    return false;
  }
};

// Calculate price for flex credits
export const calculateFlexCreditPrice = (amount) => {
  amount = parseInt(amount);
  
  // Tiered pricing
  if (amount <= 50) {
    return (amount / 50 * 9.99).toFixed(2);
  } else if (amount <= 100) {
    return (amount / 100 * 18.99).toFixed(2);
  } else if (amount <= 250) {
    return (amount / 250 * 44.99).toFixed(2);
  } else if (amount <= 500) {
    return (amount / 500 * 84.99).toFixed(2);
  } else {
    // For very large amounts, use the 500 credit rate
    return (amount / 500 * 84.99).toFixed(2);
  }
};

// Process monthly subscription renewals
// This would typically be called by a scheduled cloud function
export const processSubscriptionRenewals = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const now = new Date();
    
    const renewals = [];
    
    for (const userDoc of snapshot.docs) {
      const userData = userDoc.data();
      const subscription = userData?.subscription;
      
      if (!subscription || subscription.status !== 'active') {
        continue;
      }
      
      const nextBillingDate = new Date(subscription.nextBillingDate);
      
      // Check if it's time to renew
      if (nextBillingDate <= now) {
        // In a real implementation, you would process payment here
        // For now, we'll just renew the subscription
        
        // Calculate next billing date
        const newBillingDate = new Date(nextBillingDate.setMonth(nextBillingDate.getMonth() + 1));
        
        // Reset monthly credits and update next billing date
        await updateDoc(doc(db, 'users', userDoc.id), {
          'credits.monthly': subscription.monthlyCredits,
          'credits.lastRefillDate': now.toISOString(),
          'subscription.nextBillingDate': newBillingDate.toISOString(),
          'subscription.lastRenewalDate': now.toISOString() // Add last renewal date for tracking
        });
        
        // Update the referral system to generate new commissions for this renewal
        await updateReferralSystemWithRenewal(userDoc.id, {
          ...subscription,
          renewalDate: now.toISOString(),
          nextBillingDate: newBillingDate.toISOString()
        });
        
        renewals.push({
          userId: userDoc.id,
          email: userData.email,
          subscription: subscription.type,
          credits: subscription.monthlyCredits
        });
      }
    }
    
    return renewals;
  } catch (error) {
    console.error('Error processing subscription renewals:', error);
    throw error;
  }
};

// Map old subscription types to new ones for migration
const OLD_TO_NEW_SUBSCRIPTION_MAP = {
  'basic': SUBSCRIPTION_TYPES.PLAN_1,
  'standard': SUBSCRIPTION_TYPES.PLAN_2,
  'premium': SUBSCRIPTION_TYPES.PLAN_3
};

// Migrate old subscription types to new ones
export const migrateOldSubscriptionTypes = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const batch = writeBatch(db);
    let migrationCount = 0;
    
    snapshot.forEach(userDoc => {
      const userData = userDoc.data();
      if (userData.subscription && OLD_TO_NEW_SUBSCRIPTION_MAP[userData.subscription.type]) {
        const newType = OLD_TO_NEW_SUBSCRIPTION_MAP[userData.subscription.type];
        const userRef = doc(db, 'users', userDoc.id);
        
        batch.update(userRef, {
          'subscription.type': newType
        });
        
        migrationCount++;
      }
    });
    
    if (migrationCount > 0) {
      await batch.commit();
      console.log(`Migrated ${migrationCount} users to new subscription types`);
    }
    
    return migrationCount;
  } catch (error) {
    console.error('Error migrating subscription types:', error);
    return 0;
  }
};

// Initialize flex credit packages in Firestore if they don't exist
export const initializeFlexCreditPackages = async () => {
  try {
    const packagesRef = collection(db, 'flexCreditPackages');
    const packagesSnapshot = await getDocs(packagesRef);
    
    if (packagesSnapshot.empty) {
      console.log('No flex credit packages found, initializing default packages');
      const batch = writeBatch(db);
      
      // Small package
      batch.set(doc(packagesRef, FLEX_CREDIT_TYPES.FLEX_1), {
        name: 'Small Pack',
        description: 'Perfect for occasional use',
        credits: 100,
        price: 9.99,
        compareAtPrice: null,
        salePercentage: 0,
        features: [
          '100 Flex Credits',
          'Never Expires',
          'Use with any plan',
        ],
        enabled: true,
        sortOrder: 1,
        popular: false
      });
      
      // Medium package
      batch.set(doc(packagesRef, FLEX_CREDIT_TYPES.FLEX_2), {
        name: 'Medium Pack',
        description: 'Great for regular use',
        credits: 300,
        price: 24.99,
        compareAtPrice: null,
        salePercentage: 0,
        features: [
          '300 Flex Credits',
          'Never Expires',
          'Use with any plan',
          'Save 17% vs Small Pack',
        ],
        enabled: true,
        sortOrder: 2,
        popular: false
      });
      
      // Large package
      batch.set(doc(packagesRef, FLEX_CREDIT_TYPES.FLEX_3), {
        name: 'Large Pack',
        description: 'Best value for power users',
        credits: 1000,
        price: 69.99,
        compareAtPrice: null,
        salePercentage: 0,
        features: [
          '1000 Flex Credits',
          'Never Expires',
          'Use with any plan',
          'Save 30% vs Small Pack',
        ],
        enabled: true,
        sortOrder: 3,
        popular: true
      });
      
      // Extra large package
      batch.set(doc(packagesRef, FLEX_CREDIT_TYPES.FLEX_4), {
        name: 'XL Pack',
        description: 'Maximum value for heavy users',
        credits: 3000,
        price: 179.99,
        compareAtPrice: null,
        salePercentage: 0,
        features: [
          '3000 Flex Credits',
          'Never Expires',
          'Use with any plan',
          'Save 40% vs Small Pack',
        ],
        enabled: true,
        sortOrder: 4,
        popular: false
      });
      
      await batch.commit();
      console.log('Default flex credit packages initialized');
    } else {
      console.log('Flex credit packages already exist');
    }
  } catch (error) {
    console.error('Error initializing flex credit packages:', error);
  }
};

// Get all flex credit packages
export const getFlexCreditPackages = async () => {
  try {
    const packagesRef = collection(db, 'flexCreditPackages');
    const snapshot = await getDocs(packagesRef);
    
    if (snapshot.empty) {
      await initializeFlexCreditPackages();
      return getFlexCreditPackages();
    }
    
    const packages = {};
    snapshot.forEach(doc => {
      packages[doc.id] = doc.data();
    });
    
    return packages;
  } catch (error) {
    console.error('Error getting flex credit packages:', error);
    return DEFAULT_FLEX_CREDIT_PACKAGES;
  }
};

// Update a flex credit package
export const updateFlexCreditPackage = async (packageType, updates) => {
  try {
    // Validate package type
    if (!Object.values(FLEX_CREDIT_TYPES).includes(packageType)) {
      throw new Error(`Invalid package type: ${packageType}`);
    }
    
    const packageRef = doc(db, 'flexCreditPackages', packageType);
    const packageDoc = await getDoc(packageRef);
    
    if (!packageDoc.exists()) {
      throw new Error(`Package ${packageType} not found`);
    }
    
    const currentPackage = packageDoc.data();
    
    // Calculate compareAtPrice based on salePercentage if provided
    let compareAtPrice = updates.compareAtPrice !== undefined ? updates.compareAtPrice : currentPackage.compareAtPrice;
    let price = parseFloat(updates.price) || currentPackage.price;
    let salePercentage = updates.salePercentage !== undefined ? parseFloat(updates.salePercentage) : (currentPackage.salePercentage || 0);
    
    // If salePercentage is provided but compareAtPrice is not, calculate it
    if (salePercentage > 0 && !compareAtPrice) {
      // Ensure the price ends with the same cents as the original price
      const priceCents = Math.round((price % 1) * 100);
      const rawComparePrice = price / (1 - salePercentage / 100);
      // Format to 2 decimal places and then ensure it ends with the same cents
      const formattedPrice = Math.floor(rawComparePrice) + (priceCents / 100);
      compareAtPrice = parseFloat(formattedPrice.toFixed(2));
    } 
    // If compareAtPrice is provided but salePercentage is not, calculate it
    else if (compareAtPrice && salePercentage === 0) {
      compareAtPrice = parseFloat(compareAtPrice);
      if (compareAtPrice > price) {
        salePercentage = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
      }
    }
    // If both are 0 or null, reset both
    else if (salePercentage === 0) {
      compareAtPrice = null;
    }
    
    // Ensure compareAtPrice is never undefined
    if (compareAtPrice === undefined) {
      compareAtPrice = null;
    }
    
    // Prepare update object
    const packageUpdates = {
      name: updates.name || currentPackage.name,
      credits: parseInt(updates.credits) || currentPackage.credits,
      price: price,
      compareAtPrice: compareAtPrice,
      salePercentage: salePercentage,
      enabled: updates.enabled !== undefined ? updates.enabled : currentPackage.enabled,
      stripeProductId: updates.stripeProductId || currentPackage.stripeProductId || '',
      stripePriceId: updates.stripePriceId || currentPackage.stripePriceId || '',
      features: updates.features || currentPackage.features || [],
      sortOrder: parseInt(updates.sortOrder) || currentPackage.sortOrder,
      popular: updates.popular !== undefined ? updates.popular : currentPackage.popular,
      description: updates.description !== undefined ? updates.description : (currentPackage.description || ''),
      creditCountText: updates.creditCountText !== undefined ? updates.creditCountText : (currentPackage.creditCountText || ''),
      creditCountExplanation: updates.creditCountExplanation !== undefined ? updates.creditCountExplanation : (currentPackage.creditCountExplanation || ''),
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(packageRef, packageUpdates);
    
    return {
      ...currentPackage,
      ...packageUpdates
    };
  } catch (error) {
    console.error('Error updating flex credit package:', error);
    throw error;
  }
};

// Purchase flex credits using a specific package
export const purchaseFlexCreditPackage = async (userId, packageType) => {
  try {
    // Get the package details
    const packages = await getFlexCreditPackages();
    const packageData = packages[packageType];
    
    if (!packageData || !packageData.enabled) {
      throw new Error(`Invalid or disabled flex credit package: ${packageType}`);
    }
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    const currentFlexCredits = userData?.credits?.flex || 0;
    
    // Add the flex credits to the user's account
    await updateDoc(userRef, {
      'credits.flex': currentFlexCredits + packageData.credits,
      'credits.lastFlexPurchase': {
        date: new Date().toISOString(),
        packageType,
        packageName: packageData.name,
        credits: packageData.credits,
        price: packageData.price
      }
    });
    
    return {
      success: true,
      credits: packageData.credits,
      totalFlexCredits: currentFlexCredits + packageData.credits
    };
  } catch (error) {
    console.error('Error purchasing flex credits:', error);
    return { success: false, error: error.message };
  }
};

// Cancel a subscription with Stripe and update user record
export const cancelSubscription = async (userId, subscriptionId) => {
  try {
    // Call the Stripe service to cancel the subscription
    const result = await cancelStripeSubscription(userId, subscriptionId);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to cancel subscription');
    }
    
    // Update the user's subscription status in Firestore
    await updateDoc(doc(db, 'users', userId), {
      'subscription.status': 'canceling'
    });
    
    return { success: true, message: result.message };
  } catch (error) {
    console.error('Error canceling subscription:', error);
    return { success: false, error: error.message };
  }
};

// Set VIP status with a specific plan for a user
export const setUserVIPStatus = async (userId, planType, duration = 'unlimited') => {
  try {
    // Get the subscription plan details
    const plans = await getSubscriptionPlans();
    const plan = plans[planType];
    
    if (!plan) {
      throw new Error(`Invalid plan type: ${planType}`);
    }
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    // Calculate next billing date (1 month from now or from previous billing date)
    const now = new Date();
    const nextBillingDate = new Date(now.getTime());
    nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
    
    // Calculate end date based on duration
    let endDate = null;
    if (duration !== 'unlimited' && typeof duration === 'number' && duration > 0) {
      endDate = new Date(now.getTime());
      endDate.setMonth(endDate.getMonth() + duration);
    }
    
    // Create virtual subscription object for VIP
    const virtualSubscription = {
      type: planType,
      planName: plan.name,
      monthlyCredits: plan.monthlyCredits,
      price: plan.price,
      startDate: new Date().toISOString(),
      nextBillingDate: nextBillingDate.toISOString(),
      nextCreditResetDate: nextBillingDate.toISOString(), // Add dedicated field for credit resets
      status: 'active',
      isVIP: true,
      maxProductSelection: plan.maxProductSelection, // Add max product selection limit
      endDate: endDate ? endDate.toISOString() : null,
      duration: duration
    };
    
    // Set VIP status, plan, and reset monthly credits
    await updateDoc(userRef, {
      isVIP: true,
      vipPlan: planType,
      vipSubscription: virtualSubscription,
      maxProductSelection: plan.maxProductSelection, // Add to user document root for easy access
      'credits.monthly': plan.monthlyCredits,
      'credits.lastRefillDate': new Date().toISOString()
    });
    
    // Update referral system with subscription data
    await updateReferralSystemWithSubscription(userId, virtualSubscription);
    
    return virtualSubscription;
  } catch (error) {
    console.error('Error setting user VIP status:', error);
    throw error;
  }
};

// Remove VIP status from a user
export const removeUserVIPStatus = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData?.isVIP) {
      return false; // No VIP status to remove
    }
    
    // Prepare update object
    const updateData = {
      isVIP: false,
      vipPlan: null,
      vipSubscription: null
    };
    
    // Check if user has a regular subscription
    const hasRegularSubscription = userData?.subscription && 
                                 userData.subscription.status === 'active' && 
                                 userData.subscription.type;
    
    // Only reset monthly credits if there's no regular subscription
    if (!hasRegularSubscription) {
      updateData['credits.monthly'] = 0;
      updateData.maxProductSelection = null;
    } else {
      // If there's a regular subscription, keep its product selection limit
      updateData.maxProductSelection = userData.subscription.maxProductSelection || 0;
    }
    
    // Remove VIP status and plan
    await updateDoc(userRef, updateData);
    
    return true;
  } catch (error) {
    console.error('Error removing user VIP status:', error);
    return false;
  }
};

// Check for and remove expired VIP subscriptions
export const checkAndRemoveExpiredVIPSubscriptions = async (userId = null) => {
  try {
    const now = new Date();
    
    // If userId is provided, only check that specific user
    if (userId) {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      if (userData?.isVIP && userData?.vipSubscription?.endDate) {
        const endDate = new Date(userData.vipSubscription.endDate);
        
        if (now >= endDate) {
          console.log(`VIP subscription expired for user ${userId}. Removing VIP status.`);
          await removeUserVIPStatus(userId);
          return true;
        }
      }
      return false;
    }
    
    // If no userId provided, check all users with VIP status
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('isVIP', '==', true));
    const querySnapshot = await getDocs(q);
    
    let removedCount = 0;
    
    for (const userDoc of querySnapshot.docs) {
      const userData = userDoc.data();
      
      if (userData?.vipSubscription?.endDate) {
        const endDate = new Date(userData.vipSubscription.endDate);
        
        if (now >= endDate) {
          console.log(`VIP subscription expired for user ${userDoc.id}. Removing VIP status.`);
          await removeUserVIPStatus(userDoc.id);
          removedCount++;
        }
      }
    }
    
    console.log(`Checked for expired VIP subscriptions. Removed ${removedCount} expired subscriptions.`);
    return removedCount > 0;
  } catch (error) {
    console.error('Error checking for expired VIP subscriptions:', error);
    return false;
  }
};

// Fix existing subscription data to ensure nextCreditResetDate is set correctly
export const fixSubscriptionResetDates = async (userId) => {
  try {
    console.log(`Fixing subscription reset dates for user: ${userId}`);
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData) {
      console.log(`No user data found for ${userId}`);
      return false;
    }
    
    const hasSubscription = userData?.subscription && userData.subscription.status === 'active';
    const hasVIPSubscription = userData?.isVIP && userData?.vipSubscription;
    const now = new Date();
    const updates = {};
    let needsUpdate = false;
    
    // Fix regular subscription
    if (hasSubscription) {
      // Check if nextCreditResetDate is missing or incorrect
      const needsFixing = !userData.subscription.nextCreditResetDate || 
                          new Date(userData.subscription.nextCreditResetDate) > new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
      
      if (needsFixing) {
        console.log(`Regular subscription needs nextCreditResetDate fix for user ${userId}`);
        
        // Determine the base date for calculating the next reset
        let baseDate;
        
        if (userData.credits?.lastRefillDate) {
          baseDate = new Date(userData.credits.lastRefillDate);
          console.log(`Using lastRefillDate as base: ${baseDate.toISOString()}`);
        } else if (userData.subscription.currentPeriodStart) {
          baseDate = new Date(userData.subscription.currentPeriodStart);
          console.log(`Using currentPeriodStart as base: ${baseDate.toISOString()}`);
        } else if (userData.subscription.periodStartTimestamp) {
          baseDate = new Date(userData.subscription.periodStartTimestamp * 1000);
          console.log(`Using periodStartTimestamp as base: ${baseDate.toISOString()}`);
        } else {
          // If no dates available, use today
          baseDate = now;
          console.log(`Using current date as base: ${baseDate.toISOString()}`);
        }
        
        // Calculate next month's reset date
        const nextReset = new Date(baseDate);
        nextReset.setMonth(nextReset.getMonth() + 1);
        
        // If this date is in the past, keep adding months until we get a future date
        while (nextReset <= now) {
          nextReset.setMonth(nextReset.getMonth() + 1);
        }
        
        updates['subscription.nextCreditResetDate'] = nextReset.toISOString();
        needsUpdate = true;
        console.log(`Setting nextCreditResetDate to ${nextReset.toISOString()}`);
      }
    }
    
    // Fix VIP subscription
    if (hasVIPSubscription) {
      // Check if nextCreditResetDate is missing or incorrect
      const needsFixing = !userData.vipSubscription.nextCreditResetDate || 
                          new Date(userData.vipSubscription.nextCreditResetDate) > new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
      
      if (needsFixing) {
        console.log(`VIP subscription needs nextCreditResetDate fix for user ${userId}`);
        
        // Determine the base date for calculating the next reset
        let baseDate;
        
        if (userData.credits?.lastRefillDate) {
          baseDate = new Date(userData.credits.lastRefillDate);
          console.log(`Using lastRefillDate as base for VIP: ${baseDate.toISOString()}`);
        } else if (userData.vipSubscription.startDate) {
          baseDate = new Date(userData.vipSubscription.startDate);
          console.log(`Using startDate as base for VIP: ${baseDate.toISOString()}`);
        } else {
          baseDate = now; // Fallback to current date
          console.log(`Using current date as base for VIP: ${baseDate.toISOString()}`);
        }
        
        // Calculate next month's reset date
        const nextReset = new Date(baseDate);
        nextReset.setMonth(nextReset.getMonth() + 1);
        
        // If this date is in the past, keep adding months until we get a future date
        while (nextReset <= now) {
          nextReset.setMonth(nextReset.getMonth() + 1);
        }
        
        updates['vipSubscription.nextCreditResetDate'] = nextReset.toISOString();
        needsUpdate = true;
        console.log(`Setting VIP nextCreditResetDate to ${nextReset.toISOString()}`);
      }
    }
    
    // Apply updates if needed
    if (needsUpdate) {
      console.log(`Updating user ${userId} with fixed reset dates:`, updates);
      await updateDoc(userRef, updates);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error fixing subscription reset dates:', error);
    return false;
  }
};

// Helper function to update referral system with subscription data
const updateReferralSystemWithSubscription = async (userId, subscriptionData) => {
  try {
    // Get the user document to find their referrer
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return;
    
    const userData = userDoc.data();
    const referrerId = userData.referrerId;
    
    // If user was referred, update the referral system
    if (referrerId) {
      console.log(`Updating referral system for user ${userId} with subscription data:`, subscriptionData);
      
      const referralDocRef = doc(db, 'referralSystem', referrerId);
      const referralDoc = await getDoc(referralDocRef);
      
      if (referralDoc.exists()) {
        const referralData = referralDoc.data();
        const referredUsers = referralData.referredUsers || [];
        const referralStats = referralData.referralStats || { total: 0, active: 0 };
        
        // Find the user in the referred users list
        const userIndex = referredUsers.findIndex(ru => ru.userId === userId);
        
        // Format subscription data to ensure consistent structure
        const formattedSubscription = {
          active: subscriptionData?.status === 'active',
          planType: subscriptionData?.type || null,
          planName: subscriptionData?.planName || null,
          planPrice: subscriptionData?.price || 0,
          startDate: subscriptionData?.startDate || new Date().toISOString(), // Always ensure a startDate
          cancelDate: subscriptionData?.cancelDate || null,
          status: subscriptionData?.status || 'inactive',
          // Store the commission rate with the subscription to ensure rate changes don't affect existing subscriptions
          commissionRate: subscriptionData?.commissionRate || (referralData.commission || 10)
        };
        
        // Make sure to properly mark subscription as inactive if it's canceled or inactive
        if (subscriptionData?.status === 'canceled' || subscriptionData?.status === 'inactive') {
          formattedSubscription.active = false;
          console.log(`Marking subscription as inactive due to ${subscriptionData?.status} status`);
        }
        
        if (userIndex !== -1) {
          // Update the user's subscription data
          referredUsers[userIndex].subscription = formattedSubscription;
          
          // Update activation status if not already set
          if (!referredUsers[userIndex].activatedAt && formattedSubscription.active) {
            referredUsers[userIndex].activatedAt = new Date().toISOString();
          }
          
          console.log(`Updated subscription for referred user at index ${userIndex}:`, formattedSubscription);
        } else {
          // User not found in referred users, add them
          console.log('User not found in referred users list, adding them with subscription:', formattedSubscription);
          referredUsers.push({
            userId: userId,
            email: userData.email || '',
            fullName: userData.fullName || '',
            registeredAt: userData.createdAt || new Date().toISOString(),
            activatedAt: formattedSubscription.active ? new Date().toISOString() : null,
            subscription: formattedSubscription
          });
          
          // Update stats
          referralStats.total += 1;
        }
        
        // Count active subscriptions (users with active subscription status)
        const activeCount = referredUsers.filter(ru => ru.subscription && ru.subscription.active).length;
        referralStats.active = activeCount;
        
        console.log(`Updating referral system stats - Total: ${referralStats.total}, Active: ${activeCount}`);
        
        // Update the document
        await updateDoc(referralDocRef, {
          referredUsers: referredUsers,
          referralStats: referralStats,
          updatedAt: new Date().toISOString()
        });
        console.log('Successfully updated referral system with subscription data');
      } else {
        console.log(`No referral document found for referrer ${referrerId}`);
      }
    } else {
      console.log(`User ${userId} was not referred by anyone`);
    }
  } catch (error) {
    console.error('Error updating referral system with subscription:', error);
  }
};

// Helper function to update referral system with renewal data
const updateReferralSystemWithRenewal = async (userId, renewalData) => {
  try {
    // Get the user document to find their referrer
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return;
    
    const userData = userDoc.data();
    const referrerId = userData.referrerId;
    
    // If user was referred, update the referral system
    if (referrerId) {
      console.log(`Updating referral system for user ${userId} with renewal data:`, renewalData);
      
      const referralDocRef = doc(db, 'referralSystem', referrerId);
      const referralDoc = await getDoc(referralDocRef);
      
      if (referralDoc.exists()) {
        const referralData = referralDoc.data();
        const referredUsers = referralData.referredUsers || [];
        const referralStats = referralData.referralStats || { total: 0, active: 0, renewals: 0 };
        
        // Find the user in the referred users list
        const userIndex = referredUsers.findIndex(ru => ru.userId === userId);
        
        if (userIndex !== -1) {
          // Update the user's renewal data
          referredUsers[userIndex].renewals = referredUsers[userIndex].renewals || [];
          referredUsers[userIndex].renewals.push({
            date: renewalData.renewalDate,
            planType: renewalData.type,
            planName: renewalData.planName,
            planPrice: renewalData.price,
            nextBillingDate: renewalData.nextBillingDate
          });
          
          console.log(`Updated renewal data for referred user at index ${userIndex}:`, referredUsers[userIndex].renewals);
        } else {
          // User not found in referred users, add them
          console.log('User not found in referred users list, adding them with renewal data:', renewalData);
          referredUsers.push({
            userId: userId,
            email: userData.email || '',
            fullName: userData.fullName || '',
            registeredAt: userData.createdAt || new Date().toISOString(),
            activatedAt: null,
            subscription: null,
            renewals: [
              {
                date: renewalData.renewalDate,
                planType: renewalData.type,
                planName: renewalData.planName,
                planPrice: renewalData.price,
                nextBillingDate: renewalData.nextBillingDate
              }
            ]
          });
        }
        
        // Update stats
        referralStats.renewals += 1;
        
        console.log(`Updating referral system stats - Renewals: ${referralStats.renewals}`);
        
        // Update the document
        await updateDoc(referralDocRef, {
          referredUsers: referredUsers,
          referralStats: referralStats,
          updatedAt: new Date().toISOString()
        });
        console.log('Successfully updated referral system with renewal data');
      } else {
        console.log(`No referral document found for referrer ${referrerId}`);
      }
    } else {
      console.log(`User ${userId} was not referred by anyone`);
    }
  } catch (error) {
    console.error('Error updating referral system with renewal:', error);
  }
};
